import { useEffect, useMemo, useState } from 'react';
import { toast } from '@/lib/toast';
import { syncQuoteProductUsages } from '@/hooks/useQuoteProductStatus';
import api from '@/lib/api';
import { copyText } from '@/lib/clipboard';
import { getHubSpotUser } from '@/lib/hubspotUser';
import { useBlockStore } from '@/store/blockStore';
import type { Block, HubSpotContact, Quote, SignatureRecipient } from '@/types';
import { BlockType, QuoteStatus } from '@/types';
import {
  MAX_SIGNATURE_RECIPIENTS,
  buildRecipientId,
  readSignatureRecipients,
  withSignatureRecipients,
} from '@/lib/signatureRecipients';

type BlockStoreState = ReturnType<typeof useBlockStore.getState>;

export const UBO_SIGNERS = [
  { id: 'frank', name: 'Frank Hartog', email: 'frank@ubo.agency' },
  { id: 'wesley', name: 'Wesley Blokhuis', email: 'wesley@ubo.agency' },
  { id: 'bjorn', name: 'Bjorn Oeben', email: 'bjorn@ubo.agency' },
  { id: 'bram', name: 'Bram Kok', email: 'bram@ubo.agency' },
];

export interface ContactOption {
  id: string;
  email: string;
  name: string;
}

const normalizeContactValue = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

const contactToRecipient = (contact: ContactOption): SignatureRecipient => ({
  id: buildRecipientId(0, contact.id),
  name: contact.name,
  email: contact.email,
});

const hasSignatureTrace = (recipient: SignatureRecipient) =>
  Boolean(recipient.signature || recipient.signedAt);

const findContactForRecipient = (recipient: SignatureRecipient, contacts: ContactOption[]) =>
  contacts.find(
    (contact) =>
      (recipient.email && normalizeContactValue(contact.email) === normalizeContactValue(recipient.email)) ||
      (recipient.name && normalizeContactValue(contact.name) === normalizeContactValue(recipient.name)),
  );

const isPublishedQuoteStatus = (status?: QuoteStatus) =>
  Boolean(status && status !== QuoteStatus.DRAFT && status !== QuoteStatus.WITHDRAWN);

const isWithdrawableQuoteStatus = (status?: QuoteStatus) =>
  status === QuoteStatus.SENT || status === QuoteStatus.VIEWED;

const copyTextToClipboard = async (text: string, successMessage: string) => {
  try {
    await copyText(text);
    toast.success(successMessage);
  } catch {
    toast.error('Kopiëren lukt hier niet. Selecteer de link en kopieer deze handmatig.');
  }
};

interface UseQuotePublishingParams {
  localQuote: Quote;
  setLocalQuote: React.Dispatch<React.SetStateAction<Quote>>;
  initialQuote: Quote;
  blocks: Block[];
  dealId: string | null | undefined;
  contacts: HubSpotContact[] | undefined;
  primaryContact: HubSpotContact | undefined;
  updateQuote: (id: string, data: Partial<Quote>) => Promise<unknown>;
  setCurrentQuote: (quote: Quote | null) => void;
  updateBlock: BlockStoreState['updateBlock'];
  flushEditorAndPendingUpdates: () => Promise<boolean>;
}

/**
 * Publiceren/intrekken/delen van de offerte plus ondertekenaar-selectie:
 * share-/preview-URL's, Box Sign status, contactopties en sync van de handtekeningpagina.
 */
export function useQuotePublishing({
  localQuote,
  setLocalQuote,
  initialQuote,
  blocks,
  dealId,
  contacts,
  primaryContact,
  updateQuote,
  setCurrentQuote,
  updateBlock,
  flushEditorAndPendingUpdates,
}: UseQuotePublishingParams) {
  /** Canonieke site-URL (custom domain), anders huidige origin — voorkomt Netlify-subdomein in deelbare links. */
  const shareUrl = useMemo(() => {
    const fromEnv = import.meta.env.VITE_PUBLIC_APP_URL?.trim();
    const base = (fromEnv ? fromEnv.replace(/\/$/, '') : '') || window.location.origin;
    const slug =
      (localQuote as { uniqueUrlSlug?: string }).uniqueUrlSlug ||
      (initialQuote as { uniqueUrlSlug?: string }).uniqueUrlSlug ||
      localQuote.slug ||
      initialQuote.slug ||
      '';
    const status = localQuote.status ?? initialQuote.status;
    const sig = blocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
    const c = sig?.content as Record<string, unknown> | undefined;
    const token = typeof c?.countersignToken === 'string' ? c.countersignToken : '';
    const canCountersignStatus = status === QuoteStatus.ACCEPTED || status === QuoteStatus.EXPIRED;
    const waitingUboMedesign =
      canCountersignStatus &&
      Boolean(c?.recipientSignedAt) &&
      !c?.senderSignedAt &&
      token.length > 0 &&
      Boolean(slug);

    if (waitingUboMedesign) {
      return `${base}/countersign/${encodeURIComponent(slug)}?token=${encodeURIComponent(token)}`;
    }
    return `${base}/q/${slug}`;
  }, [localQuote, initialQuote, blocks]);

  /** Alleen `/q/:slug` — interne preview, nooit countersign-URL. */
  const publicPreviewUrl = useMemo(() => {
    const fromEnv = import.meta.env.VITE_PUBLIC_APP_URL?.trim();
    const base = (fromEnv ? fromEnv.replace(/\/$/, '') : '') || window.location.origin;
    const slug =
      (localQuote as { uniqueUrlSlug?: string }).uniqueUrlSlug ||
      (initialQuote as { uniqueUrlSlug?: string }).uniqueUrlSlug ||
      localQuote.slug ||
      initialQuote.slug ||
      '';
    return slug ? `${base}/q/${slug}` : '';
  }, [localQuote, initialQuote]);

  const [publishOpen, setPublishOpen] = useState(false);
  const [publishStep, setPublishStep] = useState<0 | 1>(0);
  const [publishLoading, setPublishLoading] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(isPublishedQuoteStatus(localQuote.status));
  const [expiryDate, setExpiryDate] = useState(() => {
    if (localQuote.expiresAt) {
      return new Date(localQuote.expiresAt).toISOString().split('T')[0];
    }
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });

  const [publishLocale, setPublishLocale] = useState<'nl' | 'en'>(() =>
    initialQuote.publicLocale === 'en' ? 'en' : 'nl',
  );

  useEffect(() => {
    setPublishLocale(localQuote.publicLocale === 'en' ? 'en' : 'nl');
  }, [localQuote.publicLocale]);

  const [signEmail, setSignEmail] = useState('');
  const [signName, setSignName] = useState('');
  const [signStatus, setSignStatus] = useState<string | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [selectedUboSigner, setSelectedUboSigner] = useState<string>('');
  const [signOnPublish, setSignOnPublish] = useState(true);
  const [senderSignature, setSenderSignature] = useState<string | null>(null);

  /** De ingelogde gebruiker als UBO-tekenaar; alleen die kan direct bij verzenden tekenen. */
  const currentUboSignerId = useMemo(() => {
    const email = getHubSpotUser()?.email?.trim().toLowerCase();
    return UBO_SIGNERS.find((s) => s.email === email)?.id ?? '';
  }, []);

  useEffect(() => {
    setSelectedContactIds([]);
    setSignEmail('');
    setSignName('');
  }, [localQuote.id, dealId]);

  useEffect(() => {
    let cancelled = false;
    api.getSignStatus(localQuote.id).then((res) => {
      if (!cancelled && res.active && res.status) setSignStatus(res.status);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [localQuote.id]);

  const contactOptions = useMemo<ContactOption[]>(() => {
    return (contacts || [])
      .filter((c) => c.properties?.email)
      .map((c) => ({
        id: c.id,
        email: c.properties.email!,
        name: [c.properties.firstname, c.properties.lastname].filter(Boolean).join(' ') || c.properties.email!,
      }));
  }, [contacts]);

  /** Voorselectie: al ingestelde tekenbevoegden op de offerte, anders het primaire dealcontact. */
  useEffect(() => {
    if (contactOptions.length === 0) return;

    const stillValid = selectedContactIds.filter((id) => contactOptions.some((c) => c.id === id));
    if (stillValid.length > 0) {
      if (stillValid.length !== selectedContactIds.length) setSelectedContactIds(stillValid);
      return;
    }

    const signatureBlock = blocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
    const configured = readSignatureRecipients(signatureBlock?.content)
      .map((recipient) => findContactForRecipient(recipient, contactOptions)?.id)
      .filter((id): id is string => Boolean(id));

    const primary = primaryContact ? contactOptions.find((c) => c.id === primaryContact.id) : null;
    const fallback = primary || contactOptions[0];
    const next = configured.length > 0 ? Array.from(new Set(configured)) : fallback ? [fallback.id] : [];
    if (next.length === 0) return;

    setSelectedContactIds(next.slice(0, MAX_SIGNATURE_RECIPIENTS));
    const first = contactOptions.find((c) => c.id === next[0]);
    if (first) {
      setSignEmail(first.email);
      setSignName(first.name);
    }
  }, [blocks, contactOptions, primaryContact, selectedContactIds]);

  /** Houd naam/e-mail van nog niet getekende tekenbevoegden gelijk aan de HubSpot-contacten. */
  useEffect(() => {
    if (contactOptions.length === 0) return;

    const primary = primaryContact ? contactOptions.find((c) => c.id === primaryContact.id) : null;
    const defaultContact = primary || contactOptions[0];

    blocks.forEach((block) => {
      if (block.type !== BlockType.SIGNATURE_PAGE || !block.content || typeof block.content !== 'object') return;

      const content = block.content as Record<string, unknown>;
      const recipients = readSignatureRecipients(content);
      if (recipients.some(hasSignatureTrace)) return;

      const nextRecipients =
        recipients.length === 0
          ? defaultContact
            ? [contactToRecipient(defaultContact)]
            : []
          : recipients.map((recipient) => {
              const contact =
                findContactForRecipient(recipient, contactOptions) ||
                (recipients.length === 1 ? defaultContact : undefined);
              return contact ? { ...recipient, name: contact.name, email: contact.email } : recipient;
            });

      if (nextRecipients.length === 0) return;

      const unchanged =
        nextRecipients.length === recipients.length &&
        nextRecipients.every(
          (recipient, index) =>
            recipient.name === recipients[index].name && recipient.email === recipients[index].email,
        );
      if (unchanged && Array.isArray(content.recipients)) return;

      void updateBlock(
        block.id,
        { content: withSignatureRecipients(content, nextRecipients) as Block['content'] },
        { external: true }
      );
    });
  }, [blocks, contactOptions, primaryContact, updateBlock]);

  const handleContactToggle = (contactId: string) => {
    setSelectedContactIds((prev) => {
      if (prev.includes(contactId)) {
        const next = prev.filter((id) => id !== contactId);
        return next.length > 0 ? next : prev;
      }
      if (prev.length >= MAX_SIGNATURE_RECIPIENTS) {
        toast.error(`Maximaal ${MAX_SIGNATURE_RECIPIENTS} tekenbevoegden aan klantzijde`);
        return prev;
      }
      return [...prev, contactId];
    });

    const contact = contactOptions.find((c) => c.id === contactId);
    if (contact && selectedContactIds.length === 0) {
      setSignEmail(contact.email);
      setSignName(contact.name);
    }
  };

  const handleCopyLink = () => {
    void copyTextToClipboard(shareUrl, 'Link gekopieerd!');
  };

  const handleCopyPreviewLink = async () => {
    if (!publicPreviewUrl) {
      toast.error('Geen preview-link beschikbaar (sla de offerte op).');
      return;
    }
    const flushed = await flushEditorAndPendingUpdates();
    if (!flushed) {
      toast.error('Laatste wijzigingen opslaan mislukt', { error: useBlockStore.getState().lastBlockSaveError });
      return;
    }
    await copyTextToClipboard(
      publicPreviewUrl,
      'Preview-link gekopieerd. Alleen intern delen; bij concept ziet de klant een duidelijke waarschuwing.',
    );
  };

  const signatureBlock = blocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
  const hasSignaturePage = Boolean(signatureBlock);
  const canWithdraw = isWithdrawableQuoteStatus(localQuote.status);
  const canSignOnPublish = hasSignaturePage && Boolean(currentUboSignerId) && selectedUboSigner === currentUboSignerId;
  // Het tekenvlak begint leeg zodra het opnieuw verschijnt; een oude handtekening mag niet ongezien mee.
  useEffect(() => {
    if (!canSignOnPublish || !signOnPublish) setSenderSignature(null);
  }, [canSignOnPublish, signOnPublish]);

  const signatureContent = signatureBlock?.content as Record<string, unknown> | undefined;
  const senderSignedName =
    signatureContent?.senderSignedAt && typeof signatureContent.senderName === 'string'
      ? signatureContent.senderName
      : null;

  const handleOpenPublish = async () => {
    if (isPublished) {
      setPublishStep(1);
      setPublishOpen(true);
    } else {
      if (!selectedUboSigner) {
        const configured = signatureContent?.senderSignerId;
        setSelectedUboSigner(
          UBO_SIGNERS.find((s) => s.id === configured)?.id || currentUboSignerId,
        );
      }
      setSignOnPublish(true);
      setSenderSignature(null);
      setPublishStep(0);
      setPublishOpen(true);
    }
  };

  const handleConfirmPublish = async () => {
    const signNow = canSignOnPublish && signOnPublish;
    if (signNow && !senderSignature) {
      toast.error('Zet eerst je handtekening, of zet direct ondertekenen uit.');
      return;
    }
    setPublishLoading(true);
    try {
      const saved = await flushEditorAndPendingUpdates();
      if (!saved) {
        throw new Error('De laatste wijzigingen konden niet worden opgeslagen. Probeer het opnieuw.');
      }
      const latestBlocks = useBlockStore.getState().blocks;
      await syncQuoteProductUsages(localQuote.id, latestBlocks);
      const uboSigner = UBO_SIGNERS.find((s) => s.id === selectedUboSigner);
      const selectedContacts = selectedContactIds
        .map((id) => contactOptions.find((c) => c.id === id))
        .filter((c): c is ContactOption => Boolean(c));
      const clientRecipients: SignatureRecipient[] =
        selectedContacts.length > 0
          ? selectedContacts.map(contactToRecipient)
          : [{ id: buildRecipientId(0), name: signName || '', email: signEmail || '' }];

      if (latestBlocks.some((b) => b.type === BlockType.SIGNATURE_PAGE)) {
        const sigBlock = latestBlocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
        if (sigBlock) {
          // Een UBO-handtekening hoort bij de versie die nu verstuurd wordt; een
          // eerdere (van vóór intrekken) vervalt en wordt hieronder zo nodig opnieuw gezet.
          const sigContent = { ...(sigBlock.content as Record<string, unknown>) };
          delete sigContent.senderSignature;
          delete sigContent.senderSignedAt;
          delete sigContent.countersignAudit;
          const updatedContent = withSignatureRecipients(
            {
              ...sigContent,
              senderName: uboSigner?.name || '',
              senderSignerId: selectedUboSigner || '',
            },
            clientRecipients,
          ) as Block['content'];

          await api.updateBlock(localQuote.id, sigBlock.id, {
            content: updatedContent,
            styles: sigBlock.styles,
            config: sigBlock.config,
            type: sigBlock.type,
          });

          // Pas na de handtekening de store bijwerken: een eerdere autosave van het
          // blok zou de handtekening op de server anders weer overschrijven.
          let finalContent = updatedContent;
          try {
            if (signNow && senderSignature) {
              const signed = await api.signQuoteAsSender(localQuote.id, {
                signerId: selectedUboSigner,
                signatureData: senderSignature,
              });
              finalContent = signed.content;
            }
          } finally {
            updateBlock(sigBlock.id, { content: finalContent }, { external: true });
          }
        }
      }

      const expiresAt = expiryDate ? new Date(expiryDate) : undefined;
      const uboSigner2 = UBO_SIGNERS.find((s) => s.id === selectedUboSigner);
      await updateQuote(localQuote.id, {
        ...localQuote,
        status: QuoteStatus.SENT,
        expiresAt,
        publicLocale: publishLocale,
        metadata: {
          ...(localQuote.metadata || {}),
          senderSignerId: selectedUboSigner || '',
          senderName: uboSigner2?.name || '',
        },
      } as any);
      setLocalQuote((prev) => ({
        ...prev,
        status: QuoteStatus.SENT,
        expiresAt: expiresAt?.toISOString(),
        publicLocale: publishLocale,
      }));
      setIsPublished(true);

      toast.success(signNow ? 'Offerte ondertekend en gepubliceerd!' : 'Offerte gepubliceerd!');

      setPublishStep(1);
    } catch (error: unknown) {
      toast.error('Publiceren mislukt', { error });
    } finally {
      setPublishLoading(false);
    }
  };

  const handleWithdrawQuote = async () => {
    setWithdrawLoading(true);
    try {
      const withdrawn = await api.withdrawQuote(localQuote.id);
      const nextQuote = {
        ...localQuote,
        status: withdrawn.status,
        updatedAt: withdrawn.updatedAt,
      };

      setLocalQuote(nextQuote);
      setCurrentQuote(nextQuote);
      setIsPublished(false);
      setWithdrawOpen(false);
      setPublishOpen(false);
      setPublishStep(0);
      toast.success('Offerte ingetrokken. De klantlink is niet meer beschikbaar.');
    } catch (err: unknown) {
      toast.error('Offerte intrekken mislukt', { error: err });
    } finally {
      setWithdrawLoading(false);
    }
  };

  return {
    shareUrl,
    publicPreviewUrl,
    publishOpen,
    setPublishOpen,
    publishStep,
    setPublishStep,
    publishLoading,
    withdrawOpen,
    setWithdrawOpen,
    withdrawLoading,
    isPublished,
    expiryDate,
    setExpiryDate,
    publishLocale,
    setPublishLocale,
    signEmail,
    setSignEmail,
    signName,
    setSignName,
    signStatus,
    selectedContactIds,
    selectedUboSigner,
    setSelectedUboSigner,
    canSignOnPublish,
    signOnPublish,
    setSignOnPublish,
    senderSignature,
    setSenderSignature,
    senderSignedName,
    contactOptions,
    handleContactToggle,
    handleCopyLink,
    handleCopyPreviewLink,
    hasSignaturePage,
    canWithdraw,
    handleOpenPublish,
    handleConfirmPublish,
    handleWithdrawQuote,
  };
}
